import { queryOptions } from "@tanstack/react-query";
import { Dispatch } from "redux";

import {
  PremiumTrialsResponse,
  RecurringTrialsResponse
} from "@seneca/trials-service-client/dist/types";

import { getClient, queryClient } from "services";

import { fetchMyPremiumTrials } from "seneca-common/features/premium-trials/service";

export const TRIALS_KEY = "trials";
export const PREMIUM_TRIALS_QUERY_KEY = [TRIALS_KEY, "fetchMyPremiumTrials"];
export const RECURRING_TRIALS_QUERY_KEY = [
  TRIALS_KEY,
  "fetchMyRecurringTrials"
];

export function fetchMyPremiumTrialsQueryOptions() {
  return queryOptions({
    queryKey: PREMIUM_TRIALS_QUERY_KEY,
    queryFn: async ({ client }) => {
      const existingTrials = client.getQueryData<PremiumTrialsResponse>(
        PREMIUM_TRIALS_QUERY_KEY
      );
      try {
        const updatedTrials =
          await getClient("trialsService").fetchPremiumTrials();
        return (
          resolvePremiumTrials({
            updatedTrials,
            existingTrials
          }) ?? null
        );
      } catch (error) {
        return null;
      }
    },
    retry: false,
    staleTime: 1000 * 30
  });
}

export function fetchMyRecurringTrialsQueryOptions() {
  return queryOptions({
    queryKey: RECURRING_TRIALS_QUERY_KEY,
    queryFn: async ({ client }) => {
      const existingTrials = client.getQueryData<RecurringTrialsResponse>(
        RECURRING_TRIALS_QUERY_KEY
      );
      try {
        const updatedTrials =
          await getClient("trialsService").fetchRecurringTrials();
        return resolveRecurringTrials({
          existingTrials,
          updatedTrials
        });
      } catch (error) {
        return null;
      }
    },
    retry: false,
    staleTime: 1000 * 30
  });
}

export function invalidatePremiumTrialQueries(dispatch: Dispatch) {
  queryClient.refetchQueries({
    queryKey: fetchMyPremiumTrialsQueryOptions().queryKey
  });
  dispatch(fetchMyPremiumTrials());
}

export function invalidateRecurringTrialQueries() {
  queryClient.invalidateQueries({
    queryKey: fetchMyRecurringTrialsQueryOptions().queryKey
  });
}

function resolveRecurringTrials({
  existingTrials,
  updatedTrials
}: {
  existingTrials: RecurringTrialsResponse | undefined;
  updatedTrials: RecurringTrialsResponse | undefined;
}) {
  if (!existingTrials || !updatedTrials) return updatedTrials;

  return Object.keys(updatedTrials).reduce<RecurringTrialsResponse>(
    (acc, key) => {
      const trial = key as keyof RecurringTrialsResponse;

      const existingTrialCount = existingTrials[trial].remaining;
      const updatedTrialCount = updatedTrials[trial].remaining;

      acc[trial] = {
        ...updatedTrials[trial],
        remaining: Math.min(existingTrialCount, updatedTrialCount)
      };

      return acc;
    },
    {} as RecurringTrialsResponse
  );
}

function resolvePremiumTrials({
  existingTrials,
  updatedTrials
}: {
  existingTrials: PremiumTrialsResponse | undefined;
  updatedTrials: PremiumTrialsResponse | undefined;
}) {
  if (!existingTrials || !updatedTrials) return updatedTrials;

  const resolvedTrials = Object.keys(updatedTrials.trials).reduce<
    PremiumTrialsResponse["trials"]
  >(
    (acc, key) => {
      const trial = key as keyof PremiumTrialsResponse["trials"];

      const existingTrialCount = existingTrials.trials[trial].remaining;
      const updatedTrialCount = updatedTrials.trials[trial].remaining;

      acc[trial] = {
        ...updatedTrials.trials[trial],
        remaining: Math.min(existingTrialCount, updatedTrialCount)
      };

      return acc;
    },
    {} as PremiumTrialsResponse["trials"]
  );

  return {
    ...updatedTrials,
    trials: resolvedTrials
  };
}
